import {computed, inject, Injectable, signal} from '@angular/core';
import {environment} from '@epg-apps/cdh-environments';
import {HttpClient} from '@angular/common/http';
import {
  StorageEnvironment,
  StorageEnvironmentState,
} from '@epg-apps/cdh-models';
import {Observable, take} from 'rxjs';

@Injectable({
  providedIn: 'root',
})
export class StorageEnvironmentService {
  private static readonly STORAGE_ENVIRONMENT_CDH_API =
    environment.cdhApi + 'storage-environments';
  private readonly http = inject(HttpClient);

  private readonly storageEnvironmentState = signal<StorageEnvironmentState>({
    storageEnvironments: [],
    isLoading: false,
  });

  storageEnvironments = computed(
    () => this.storageEnvironmentState().storageEnvironments,
  );

  public loadStorageEnvironments(customerId = ''): void {
    this.getStorageEnvironments(customerId)
      .pipe(take(1))
      .subscribe((customersPage) => {
        this.setStorageEnvironments(customersPage);
      });
  }

  public getStorageEnvironments(
    customerId: string,
  ): Observable<StorageEnvironment[]> {
    const headerOptions = {
      params: {
        customerId: customerId,
      },
    };
    this.setIsLoading();
    return this.http.get<StorageEnvironment[]>(
      StorageEnvironmentService.STORAGE_ENVIRONMENT_CDH_API,
      headerOptions,
    );
  }

  public setStorageEnvironments(
    storageEnvironments: StorageEnvironment[],
  ): void {
    this.storageEnvironmentState.update((state): StorageEnvironmentState => {
      return {
        ...state,
        isLoading: false,
        storageEnvironments: storageEnvironments,
      };
    });
  }

  private setIsLoading(): void {
    this.storageEnvironmentState.update((state) => {
      return {
        ...state,
        isLoading: true,
      };
    });
  }

  public createStorageEnvironmentAndReload(
    nameStorageEnvironment: string,
    selectedEnvironmentId: string,
  ): void {
    this.createStorageEnvironment(nameStorageEnvironment, selectedEnvironmentId)
      .pipe(take(1))
      .subscribe((storageEnvironments) => {
        this.loadStorageEnvironments(storageEnvironments.customerId);
      });
  }

  public updateStorageEnvironmentAndReload(
    storageEnvironmentId: string,
    name: string,
    environmentId: string | null,
  ): void {
    this.updateStorageEnvironment(storageEnvironmentId, name, environmentId)
      .pipe(take(1))
      .subscribe((storageEnvironment) => {
        this.loadStorageEnvironments(storageEnvironment.customerId);
      });
  }

  public createStorageEnvironment(
    nameStorageEnvironment: string,
    selectedEnvironmentId: string,
  ): Observable<StorageEnvironment> {
    const body = {
      environmentId: selectedEnvironmentId,
      name: nameStorageEnvironment,
    };
    return this.http.post<StorageEnvironment>(
      StorageEnvironmentService.STORAGE_ENVIRONMENT_CDH_API,
      body,
    );
  }

  public updateStorageEnvironment(
    storageEnvironmentId: string,
    name: string,
    environmentId: string | null,
  ): Observable<StorageEnvironment> {
    const body = {
      environmentId,
      name,
    };
    return this.http.put<StorageEnvironment>(
      StorageEnvironmentService.STORAGE_ENVIRONMENT_CDH_API +
        '/' +
        storageEnvironmentId,
      body,
    );
  }
}
